// Онбординг кампании в gn-стиле: экраны подготовки до READY (ожидание игроков →
// предложение мира → голосование → генерация) + реал роли перед входом в игру.
// Заменяет легаси-fallback в GnCampaignShell (роутинг по setupStage/worldSelection.phase).
// Мутации идут через action() (он перезагружает setup). Изолированная .gn-* разметка;
// инвайт/код/ростер/досье-словарь переиспользованы из OwnerPanel/HqScreen.

import { useState, type ReactNode } from "react";
import { api, type CampaignSetup, type SetupMember, type WorldProposal } from "../api";
import { initials } from "../cycle/shared";
import { CycleTimingCard } from "./CycleTimingCard";

const GEN = "/assets/game-ui/gen";
type GnAction = (label: string, run: () => Promise<unknown>) => Promise<boolean>;

// Локальная копия проверок черновика мира (pure; в легаси PreparationView).
function proposalDraftChecks(worldName: string, concept: string): { label: string; done: boolean }[] {
  const words = concept.trim().match(/[\p{L}\p{N}][\p{L}\p{N}'’_-]*/gu) ?? [];
  const uniqueWords = new Set(words.filter((w) => w.length >= 3).map((w) => w.toLocaleLowerCase("ru")));
  const noLongRun = !/(.)\1{6,}/iu.test(`${worldName} ${concept}`);
  return [
    { label: "Название — от 3 символов", done: worldName.trim().length >= 3 },
    { label: "Описание — от 180 символов", done: concept.trim().length >= 180 },
    { label: "Не меньше 24 слов, 12 разных", done: words.length >= 24 && uniqueWords.size >= 12 },
    { label: "Без длинных повторов символов", done: noLongRun },
  ];
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function Check({ done, children }: { done: boolean; children: ReactNode }) {
  return (
    <div className={`gn-chk${done ? " done" : ""}`}>
      <span className="gn-chk-d" />
      {children}
    </div>
  );
}

function MemberRow({ member, ready }: { member: SetupMember; ready: boolean }) {
  return (
    <div className={`gn-mrow${member.isCurrentUser ? " me" : ""}`}>
      <span className="gn-mav">{initials(member.displayName)}</span>
      <span className="gn-minfo">
        <span className="gn-mname">
          <b>{member.displayName}</b>
          {member.isCurrentUser && <span className="gn-badge you">вы</span>}
        </span>
        <div className="gn-off">{member.memberRole === "OWNER" ? "Организатор" : "Участник"}</div>
      </span>
      <span className={`gn-obadge${ready ? " ok" : ""}`}>{ready ? "готов" : "ждёт"}</span>
    </div>
  );
}

function InvitePanel({ campaignId, setup, action }: { campaignId: string; setup: CampaignSetup; action: GnAction }) {
  const [invite, setInvite] = useState("");
  const code = setup.campaign.inviteCode;
  const atCap = setup.campaign.memberCount >= 10;
  async function send() {
    const target = invite.trim();
    if (!target) return;
    if (await action("Отправляем приглашение…", () => api.inviteCampaignUser(campaignId, target))) setInvite("");
  }
  return (
    <div className="gn-panel">
      <span className="gn-eyebrow">Позвать игроков</span>
      {code && (
        <div className="gn-code">
          <div className="gn-cl">
            <small>Код приглашения</small>
            <b>{code}</b>
          </div>
          <button type="button" className="gn-darkbtn gn-cp" onClick={() => void navigator.clipboard?.writeText(code)}>
            Скопировать
          </button>
        </div>
      )}
      <div className="gn-invrow">
        <input
          className="gn-oinput"
          placeholder="@username или ID"
          value={invite}
          onChange={(e) => setInvite(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void send()}
        />
        <button type="button" className="gn-invbtn" disabled={!invite.trim() || atCap} onClick={() => void send()}>
          Пригласить
        </button>
      </div>
      {setup.pendingInvitations.length > 0 && (
        <div className="gn-pend">
          <div className="gn-pt">Ожидают ответа · {setup.pendingInvitations.length}</div>
          {setup.pendingInvitations.map((inv) => (
            <div className="gn-prow" key={inv.id}>
              <span className="gn-pav">{initials(inv.invitedUserDisplayName)}</span>
              <span className="gn-pn">
                {inv.invitedUserDisplayName}
                {inv.invitedUserUsername ? ` · @${inv.invitedUserUsername}` : ""}
              </span>
              <span className="gn-ps">приглашён</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProposalCardMini({ proposal }: { proposal: WorldProposal }) {
  return (
    <div className="gn-prop">
      <div className="gn-prop-h">
        <h3>{proposal.worldName}</h3>
        <span className={`gn-obadge${proposal.isEligibleForVoting ? " ok" : ""}`}>
          {proposal.isEligibleForVoting ? "допущено" : "на доработку"}
        </span>
      </div>
      <div className="gn-prop-auth">{proposal.authorDisplayName}{proposal.isMine ? " · вы" : ""}</div>
      <p>{proposal.concept}</p>
    </div>
  );
}

function ProposalScreen({ campaignId, setup, action }: { campaignId: string; setup: CampaignSetup; action: GnAction }) {
  const sel = setup.worldSelection;
  const ready = new Set(sel.readyUserIds);
  const approved = sel.proposals.filter((p) => p.isEligibleForVoting).length;
  const [worldName, setWorldName] = useState("");
  const [concept, setConcept] = useState("");
  const checks = proposalDraftChecks(worldName, concept);
  const canSend = checks.every((c) => c.done);
  const words = concept.trim().match(/[\p{L}\p{N}][\p{L}\p{N}'’_-]*/gu)?.length ?? 0;

  async function submit() {
    if (!canSend) return;
    if (await action("Отправляем мир на проверку…", () => api.submitWorldProposal(campaignId, worldName.trim(), concept.trim()))) {
      setWorldName("");
      setConcept("");
    }
  }

  return (
    <>
      {/* Описание мира — доступно сразу, не дожидаясь сбора игроков */}
      {sel.canSubmitProposal ? (
        <>
          <div className="gn-panel">
            <span className="gn-eyebrow gn-eyebrow-p">Опишите мир</span>
            <p className="gn-omuted" style={{ margin: "2px 0 11px" }}>Можно прямо сейчас, в одиночку — участники подтянутся и добавят свои варианты. Голосование откроется, когда наберётся хотя бы 3 игрока.</p>
            <label className="gn-lab">Название мира</label>
            <input className="gn-oinput" maxLength={160} value={worldName} onChange={(e) => setWorldName(e.target.value)} placeholder="Например, Затонувший мегаполис" />
            <div className={`gn-ocounter${worldName.length >= 3 ? " ok" : ""}`}>{worldName.length} / 160</div>
            <label className="gn-lab" style={{ marginTop: "11px" }}>Концепция мира</label>
            <textarea className="gn-ta" rows={7} maxLength={6000} value={concept} onChange={(e) => setConcept(e.target.value)} placeholder="Что за мир, какой кризис, чем занимается Совет…" />
            <div className={`gn-ocounter${concept.trim().length >= 180 ? " ok" : ""}`}>{concept.length} / 6000 · {words} слов</div>
          </div>
          <div className="gn-panel">
            <span className="gn-eyebrow gn-eyebrow-p">Что стоит описать</span>
            {checks.map((c, i) => (
              <Check done={c.done} key={i}>{c.label}</Check>
            ))}
          </div>
          <button type="button" className={`gn-cta${canSend ? "" : " gn-dis"}`} disabled={!canSend} onClick={() => void submit()}>
            Предложить мир
          </button>
          {sel.canSkipProposal && (
            <button type="button" className="gn-secbtn" onClick={() => void action("Пропускаем предложение…", () => api.skipWorldProposal(campaignId))}>
              Не предлагать — я за чужой мир
            </button>
          )}
        </>
      ) : (
        <div className="gn-panel">
          <span className="gn-eyebrow">Ваше решение</span>
          <p className="gn-omuted" style={{ marginTop: "2px" }}>
            {sel.myProposalSkipped ? "Вы пропустили предложение — проголосуете за чужой мир." : "Ваш мир отправлен. Ждём остальных участников."}
          </p>
        </div>
      )}

      {sel.proposals.length > 0 && (
        <div className="gn-panel">
          <span className="gn-eyebrow">Предложенные миры · {sel.proposals.length}</span>
          {sel.proposals.map((p) => (
            <ProposalCardMini proposal={p} key={p.id} />
          ))}
        </div>
      )}

      {/* Сбор игроков идёт параллельно с описанием мира */}
      <div className="gn-panel">
        <span className="gn-eyebrow">Состав · {setup.members.length} из 10</span>
        {setup.members.map((m) => (
          <MemberRow key={m.campaignMemberId} member={m} ready={ready.has(m.userId)} />
        ))}
      </div>
      <InvitePanel campaignId={campaignId} setup={setup} action={action} />
      {setup.canManage && (
        <div className="gn-panel">
          <span className="gn-eyebrow">Настройки цикла</span>
          <p className="gn-omuted">Сколько длится каждый этап игрового цикла. Позже можно изменить в настройках владельца.</p>
          <CycleTimingCard campaignId={campaignId} value={setup.cycle.timingSettings} action={action} />
        </div>
      )}
      <div className="gn-panel">
        <span className="gn-eyebrow gn-eyebrow-p">Готовность к старту</span>
        <Check done={setup.members.length >= 3}>Набрано хотя бы 3 игрока ({setup.members.length}/10)</Check>
        <Check done={approved >= 1}>Хотя бы один мир предложен и допущен</Check>
      </div>

      {setup.canManage && sel.canOpenVoting && (
        <button type="button" className="gn-cta" onClick={() => void action("Открываем голосование…", () => api.openWorldVoting(campaignId))}>
          {sel.approvedProposalCount === 1 ? "Выбрать единственную концепцию" : "Запустить голосование"}
        </button>
      )}
    </>
  );
}

function VotingScreen({ campaignId, setup, action }: { campaignId: string; setup: CampaignSetup; action: GnAction }) {
  const sel = setup.worldSelection;
  const ranked = [...sel.proposals]
    .filter((p) => p.isEligibleForVoting)
    .sort((a, b) => b.voteCount - a.voteCount);
  const others = sel.proposals.filter((p) => !p.isEligibleForVoting);

  return (
    <>
      {sel.generationFailed && (
        <div className="gn-panel gn-failcard">
          <b>Мир не сгенерировался</b>
          <p>{sel.generationFailureMessage || "Попробуйте создать мир заново."}</p>
        </div>
      )}
      {ranked.map((p, i) => {
        const mine = sel.myVoteProposalId === p.id;
        return (
          <div className={`gn-prop${i === 0 ? " win" : ""}`} key={p.id}>
            <div className="gn-prop-top">
              <span className="gn-prank">{i + 1}</span>
              <h3>{p.worldName}</h3>
              <span className="gn-pvotes">
                <b>{p.voteCount}</b>
                <small>голос.</small>
              </span>
            </div>
            <span className="gn-obadge ok">допущено</span>
            <div className="gn-prop-auth">{p.authorDisplayName}{p.isMine ? " · вы" : ""}</div>
            <p>{p.concept}</p>
            <button
              type="button"
              className={`gn-votebtn${mine ? " voted" : ""}`}
              disabled={mine || !sel.canVote}
              onClick={() => void action("Сохраняем ваш голос…", () => api.castWorldVote(campaignId, p.id))}
            >
              {mine ? "✓ Ваш голос здесь" : "Голосовать за этот мир"}
            </button>
          </div>
        );
      })}

      {others.map((p) => (
        <ProposalCardMini proposal={p} key={p.id} />
      ))}

      {setup.canManage && sel.canFinalizeVoting && (
        <button type="button" className="gn-cta" onClick={() => void action("Создаём мир…", () => api.finalizeWorldVoting(campaignId))}>
          {sel.generationFailed ? "Создать мир заново" : "Завершить выбор и создать мир"}
        </button>
      )}
      {setup.canManage && !sel.canFinalizeVoting && sel.canForceFinalizeVoting && (
        <button type="button" className="gn-cta" onClick={() => void action("Создаём мир…", () => api.forceFinalizeWorldVoting(campaignId))}>
          Завершить выбор досрочно
        </button>
      )}
      <p className="gn-omuted" style={{ textAlign: "center" }}>Победит мир с большинством голосов. После — генерация и раздача ролей.</p>
    </>
  );
}

function GeneratingScreen({ setup }: { setup: CampaignSetup }) {
  const phase = setup.worldSelection.phase;
  const steps = [
    { key: "concept", label: "Концепция выбрана", done: true, now: false },
    { key: "world", label: "Создаётся мир: ресурсы, фракции, угрозы", done: false, now: phase === "WORLD_GENERATING" },
    { key: "roles", label: "Раздаются роли и полномочия", done: false, now: phase === "RANDOM_ROLE_ASSIGNMENT" },
    { key: "brief", label: "Готовятся личные брифинги", done: false, now: phase === "PRIVATE_BRIEFING" },
  ];
  const order = ["WORLD_GENERATING", "RANDOM_ROLE_ASSIGNMENT", "PRIVATE_BRIEFING"];
  const idx = order.indexOf(phase);
  steps.forEach((s, i) => {
    if (i > 0 && idx >= 0 && i - 1 < idx) s.done = true;
  });
  const pct = phase === "WORLD_GENERATING" ? 38 : phase === "RANDOM_ROLE_ASSIGNMENT" ? 72 : 90;

  return (
    <div className="gn-gen">
      <div className="gn-gen-med">
        <img src={`${GEN}/med_situation.png`} alt="" />
      </div>
      <h2>Мир формируется…</h2>
      <p className="gn-omuted" style={{ textAlign: "center" }}>
        Совет уже собран. Из выбранной концепции рождается город, его силы и ваши роли. Это занимает минуту-две — экран обновится сам.
      </p>
      <div className="gn-gpbar">
        <i style={{ width: `${pct}%` }} />
      </div>
      <div className="gn-gsteps">
        {steps.map((s) => (
          <div className={`gn-gstep${s.done ? " done" : ""}${s.now ? " now" : ""}`} key={s.key}>
            <span className="gn-gd" />
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export function OnboardingScreen({
  campaignId,
  setup,
  action,
  onBack,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: GnAction;
  onBack: () => void;
}) {
  const phase = setup.worldSelection.phase;
  const generating = phase === "WORLD_GENERATING" || phase === "RANDOM_ROLE_ASSIGNMENT" || phase === "PRIVATE_BRIEFING";
  const subtitle = generating
    ? "Создание мира"
    : phase === "WORLD_VOTING"
      ? "Голосование за мир"
      : "Сбор игроков · описание мира";

  return (
    <>
      <div className="gn-top">
        <button type="button" className="gn-back" onClick={onBack} aria-label="К кампаниям">
          <BackIcon />
        </button>
        <div className="gn-tt">
          <b>{setup.campaign.title}</b>
          <span>{subtitle}</span>
        </div>
        <span className="gn-cog-spacer" aria-hidden="true" />
      </div>
      <div className="gn-screen">
        <div className="gn-body">
          {generating ? (
            <GeneratingScreen setup={setup} />
          ) : phase === "WORLD_VOTING" ? (
            <VotingScreen campaignId={campaignId} setup={setup} action={action} />
          ) : (
            <ProposalScreen campaignId={campaignId} setup={setup} action={action} />
          )}
        </div>
      </div>
    </>
  );
}

export function RoleRevealScreen({
  setup,
  onEnter,
}: {
  campaignId: string;
  setup: CampaignSetup;
  onEnter: () => void;
}) {
  const hub = setup.commandHub;
  const me = setup.members.find((m) => m.isCurrentUser);
  const myChar = me ? setup.characters.find((c) => c.campaignMemberId === me.campaignMemberId) : undefined;
  const priv = setup.myPrivateProfile;
  const name = hub?.character.displayName ?? myChar?.displayName ?? me?.displayName ?? "Участник Совета";
  const role = myChar?.publicTitle ?? priv?.officeTitle ?? hub?.character.title ?? "Участник Совета";
  const gender = hub?.character.genderLabel;
  const duties = myChar?.publicDuties ?? hub?.character.dutiesFull;
  const power = myChar?.specialPower ?? hub?.character.specialPowerFull;
  const goal = priv?.personalGoalProgress;
  const briefing = priv?.privateBriefing;
  const worldDesc = (setup.world?.summary || setup.world?.concept || "").trim();
  const prologue = (setup.world?.openingNarrative || "").trim();
  const [prologueOpen, setPrologueOpen] = useState(false);

  return (
    <>
      <div className="gn-top">
        <div className="gn-tt" style={{ textAlign: "center" }}>
          <b>{setup.world?.worldName ?? setup.campaign.title}</b>
          <span>Ваша роль в Совете</span>
        </div>
      </div>
      <div className="gn-screen">
        <div className="gn-body">
          {worldDesc && (
            <div className="gn-panel">
              <span className="gn-eyebrow">Мир{setup.world?.worldName ? ` · ${setup.world.worldName}` : ""}</span>
              <p className="gn-mtext" style={{ whiteSpace: "pre-line" }}>{worldDesc}</p>
              {prologue && (
                <>
                  <button type="button" className="gn-secbtn" onClick={() => setPrologueOpen((o) => !o)}>
                    {prologueOpen ? "Свернуть пролог" : "Читать пролог"}
                  </button>
                  {prologueOpen && (
                    <p className="gn-mtext" style={{ whiteSpace: "pre-line", marginTop: "10px" }}>{prologue}</p>
                  )}
                </>
              )}
            </div>
          )}
          <div className="gn-panel gn-rolehero">
            <span className="gn-eyebrow">Мир создан · вы назначены</span>
            <span className="gn-rolemed">{me ? <span className="gn-ini">{initials(name)}</span> : null}</span>
            <div className="gn-rname">{name}</div>
            <div className="gn-orole">{[role, gender].filter(Boolean).join(" · ")}</div>
          </div>

          {duties && (
            <div className="gn-panel">
              <span className="gn-eyebrow">Публичная роль</span>
              <p className="gn-mtext">{duties}</p>
              {power && (
                <div className="gn-powerline">
                  <img src={`${GEN}/role_office.png`} alt="" />
                  <p>{power}</p>
                </div>
              )}
            </div>
          )}

          {goal && (
            <div className="gn-panel">
              <span className="gn-eyebrow gn-priv">Личная цель · только для вас</span>
              <div className="gn-gtitle">{goal.title}</div>
              {goal.description && <p className="gn-mtext">{goal.description}</p>}
            </div>
          )}

          {briefing && (
            <div className="gn-panel">
              <span className="gn-eyebrow">Служебный брифинг</span>
              <p className="gn-mtext">{briefing}</p>
            </div>
          )}

          <button type="button" className="gn-cta" onClick={onEnter}>
            Войти в игру ›
          </button>
        </div>
      </div>
    </>
  );
}
